import clsx from 'clsx';
import React, { useCallback, useRef } from 'react';

export type SidebarTabItem = {
  className?: string;
  id: string;
  label: string;
};

type SidebarTabsProps = {
  activeTabId: string;
  ariaLabel: string;
  onChange: (id: string) => void;
  tabs: SidebarTabItem[];
};

function SidebarTabs({ activeTabId, ariaLabel, onChange, tabs }: Readonly<SidebarTabsProps>): JSX.Element {
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const isProgrammaticFocus = useRef(false);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLButtonElement>, currentIndex: number) => {
      const lastIndex = tabs.length - 1;

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        const nextIndex = currentIndex < lastIndex ? currentIndex + 1 : 0;
        isProgrammaticFocus.current = true;
        onChange(tabs[nextIndex].id);
        tabRefs.current[nextIndex]?.focus();
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        const prevIndex = currentIndex > 0 ? currentIndex - 1 : lastIndex;
        isProgrammaticFocus.current = true;
        onChange(tabs[prevIndex].id);
        tabRefs.current[prevIndex]?.focus();
      } else if (event.key === 'Home') {
        event.preventDefault();
        isProgrammaticFocus.current = true;
        onChange(tabs[0].id);
        tabRefs.current[0]?.focus();
      } else if (event.key === 'End') {
        event.preventDefault();
        isProgrammaticFocus.current = true;
        onChange(tabs[lastIndex].id);
        tabRefs.current[lastIndex]?.focus();
      }
    },
    [tabs, onChange]
  );

  return (
    <div aria-label={ariaLabel} className="sidebar-tabs" role="tablist">
      {tabs.map((tab, index) => {
        const isActive = tab.id === activeTabId;

        function handleClick(): void {
          onChange(tab.id);
        }

        function handleFocus(): void {
          if (isProgrammaticFocus.current) {
            isProgrammaticFocus.current = false;
            return;
          }
          onChange(tab.id);
        }

        function handleKeyDownForTab(event: React.KeyboardEvent<HTMLButtonElement>): void {
          handleKeyDown(event, index);
        }

        return (
          <button
            key={tab.id}
            ref={(el) => {
              tabRefs.current[index] = el;
            }}
            aria-selected={isActive}
            className={clsx('sidebar-tabs__tab', { 'sidebar-tabs__tab--active': isActive }, tab.className)}
            id={`sidebar-tab-${tab.id}`}
            onFocus={handleFocus}
            onClick={handleClick}
            onKeyDown={handleKeyDownForTab}
            role="tab"
            tabIndex={0}
            type="button"
          >
            <span className="sidebar-tabs__tab-label">{tab.label}</span>
          </button>
        );
      })}
    </div>
  );
}

export default SidebarTabs;
